import { useMemo } from "react";
import { useAppSelector } from "@/app/store";
import { getRoleTypeNameFromUser } from "@/utils";
import type { PermissionType, SubscriptionFeaturesType } from "@/types/graphql";

export const useHasPermission = (moduleNames: string[] = [], feature = "") => {
  //#region useMemo
  const mergedCodes = useMemo(() => {
    const commonPermissions = ["developer"];
    return commonPermissions.concat(moduleNames);
  }, [moduleNames]);
  //#endregion useMemo

  //#region Data from Redux
  const user = useAppSelector((state) => state.auth.user);
  const rolesList = useAppSelector((state) => state.auth.rolesList);
  const tenantsList = useAppSelector((state) => state.auth.tenantsList);
  const featuresList = useAppSelector((state) => state.auth.featuresList);

  const roleType = getRoleTypeNameFromUser(user);

  const subscriptionActive = useMemo(() => {
    if (roleType === "company") {
      const expireAtValue = tenantsList?.[0]?.subscriptionExpireAt;
      const expireAt = expireAtValue
        ? new Date(expireAtValue as string | number | Date)
        : null;
      const now = new Date();
      return expireAt ? expireAt > now : false;
    }
    return true;
  }, [roleType, tenantsList]);

  const allowFeature = useMemo(() => {
    return (
      roleType === "super" ||
      roleType === "system" ||
      roleType === "partner" ||
      (featuresList?.some(
        (item: SubscriptionFeaturesType) => item?.key === feature,
      ) &&
        subscriptionActive) ||
      feature === "common"
    );
  }, [roleType, featuresList, feature, subscriptionActive]);

  //#endregion Data

  //#region useMemo
  const rolePermissions = useMemo(() => {
    return (rolesList?.[0]?.modules || []).filter(
      (module: unknown): module is string => Boolean(module),
    );
  }, [rolesList]);

  const userPermissions = useMemo(() => {
    const permissionsByRole = (user?.permissions ||
      []) as Array<PermissionType | null>;

    if (!permissionsByRole.length) {
      return [];
    }

    const currentRoleModules = permissionsByRole
      .filter((item) => item?.role === user?.currentRoleKey)
      .flatMap((item) => item?.modules || [])
      .filter((module): module is string => Boolean(module));

    if (currentRoleModules.length) {
      return currentRoleModules;
    }

    return permissionsByRole
      .flatMap((item) => item?.modules || [])
      .filter((module): module is string => Boolean(module));
  }, [user?.permissions, user?.currentRoleKey]);

  const permissions = useMemo(() => {
    if (rolePermissions.length) {
      return rolePermissions;
    }

    return userPermissions;
  }, [rolePermissions, userPermissions]);

  const codes = useMemo(() => {
    return mergedCodes?.length ? mergedCodes : [];
  }, [mergedCodes]);

  const allowPermissions = useMemo(() => {
    if (!codes?.length) {
      return false;
    }

    if (codes.includes("allow-to-all")) {
      return true;
    }

    if (!permissions?.length) {
      return false;
    }

    return codes.some(
      (moduleName) => moduleName && permissions.includes(moduleName),
    );
  }, [codes, permissions]);
  //#endregion useMemo

  return {
    loading: false,
    allow: Boolean(allowPermissions && allowFeature),
  };
};
